import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { withApiPrefix } from '../../services/api';
import styles from './SentenceStudy.module.css';
import { LoaderIcon, HeartIcon, SettingsIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';
import WordModal from '../reader/components/WordModal/WordModal';

const getSentenceKey = (sentence) => (
  sentence?.id
  ?? sentence?.favorite_id
  ?? sentence?.favoriteId
  ?? sentence?.sentence_id
  ?? sentence?.sentenceId
);

const shuffleWords = (words) => {
  const array = [...words];
  for (let i = array.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const normalizeWord = (word) => (word || '').replace(/[^A-Za-z']/g, '').toLowerCase();

const SentencePuzzle = ({ sentenceKey, contentEn, maskRatio, onMaskRatioChange }) => {
  const [maskedWords, setMaskedWords] = useState([]);
  const [selectedWords, setSelectedWords] = useState([]);
  const [currentMaskIndex, setCurrentMaskIndex] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | success | error
  const [shuffleSeed, setShuffleSeed] = useState(0);

  const tokenizedSentence = useMemo(() => {
    if (!contentEn) {
      return [];
    }
    const tokens = contentEn.split(/(\s+)/).filter(Boolean);
    return tokens.map((text, index) => ({
      id: `${sentenceKey || 'sentence'}-tok-${index}`,
      text,
      isWord: /[A-Za-z']+/.test(text) && /[A-Za-z']+/.test(text.replace(/[^A-Za-z']/g, '')),
      position: index,
    }));
  }, [contentEn, sentenceKey]);

  const maskedWordTokens = useMemo(() => tokenizedSentence.filter((token) => token.isWord), [tokenizedSentence]);

  useEffect(() => {
    if (maskedWordTokens.length === 0) {
      setMaskedWords([]);
      setSelectedWords([]);
      setStatus('idle');
      return;
    }

    const ratio = Number.isFinite(maskRatio) ? Math.min(Math.max(maskRatio, 0.1), 1) : 0.3;
    const wordsToMask = Math.min(
      Math.max(1, Math.round(maskedWordTokens.length * ratio)),
      Math.max(1, maskedWordTokens.length - 1)
    );
    const shuffledIndices = shuffleWords(maskedWordTokens.map((token, index) => ({ token, index })));
    const chosen = shuffledIndices.slice(0, wordsToMask).map(({ token, index }) => ({
      id: `${sentenceKey || 'sentence'}-mask-${index}`,
      originalTokenId: token.id,
      text: token.text,
      position: token.position,
    }));

    setMaskedWords(chosen);
    setSelectedWords([]);
    setCurrentMaskIndex(0);
    setStatus('idle');
  }, [maskedWordTokens, sentenceKey, maskRatio, shuffleSeed]);

  const sortedMaskedWords = useMemo(
    () => [...maskedWords].sort((a, b) => a.position - b.position),
    [maskedWords]
  );

  const puzzleOptions = useMemo(
    () => shuffleWords(maskedWords),
    [maskedWords]
  );

  const handleSelectWord = useCallback((wordId) => {
    const clickedWord = maskedWords.find((item) => item.id === wordId);
    if (!clickedWord) {
      return;
    }

    if (selectedWords.find((item) => item.id === wordId)) {
      return;
    }

    const expected = sortedMaskedWords[currentMaskIndex];
    if (!expected) {
      setStatus('error');
      return;
    }

    if (normalizeWord(expected.text) !== normalizeWord(clickedWord.text)) {
      setStatus('error');
      return;
    }

    const mappedWord = {
      ...clickedWord,
      originalTokenId: expected.originalTokenId,
      position: expected.position,
    };

    setSelectedWords((prev) => [...prev, mappedWord]);
    setCurrentMaskIndex((prev) => prev + 1);
    setStatus('idle');
  }, [maskedWords, sortedMaskedWords, currentMaskIndex, selectedWords]);

  const handleRemoveWord = useCallback((wordId) => {
    let removed = false;
    setSelectedWords((prev) => {
      if (!prev.length) {
        return prev;
      }
      const last = prev[prev.length - 1];
      if (last.id !== wordId) {
        return prev;
      }
      removed = true;
      return prev.slice(0, prev.length - 1);
    });
    if (removed) {
      setCurrentMaskIndex((prev) => Math.max(0, prev - 1));
      setStatus('idle');
    }
  }, []);

  const handleReset = useCallback(() => {
    setSelectedWords([]);
    setCurrentMaskIndex(0);
    setStatus('idle');
  }, []);

  const handleShuffle = useCallback(() => {
    setShuffleSeed((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!maskedWords.length) {
      setStatus('idle');
      return;
    }
    if (selectedWords.length === 0) {
      setStatus('idle');
      return;
    }
    if (selectedWords.length === maskedWords.length) {
      const sortedMasked = [...maskedWords].sort((a, b) => a.position - b.position);
      const sortedSelected = [...selectedWords].sort((a, b) => a.position - b.position);
      const isCorrect = sortedMasked.every((original, index) => (
        normalizeWord(original.text) === normalizeWord(sortedSelected[index]?.text)
      ));
      setStatus(isCorrect ? 'success' : 'error');
      setCurrentMaskIndex(sortedMasked.length);
    } else {
      setStatus('idle');
    }
  }, [maskedWords, selectedWords]);

  if (!maskedWords.length) {
    return null;
  }

  const maskedTokenIds = new Set(maskedWords.map((item) => item.originalTokenId));
  const displayedSentenceFragments = tokenizedSentence.map((token) => {
    if (!token.isWord || !maskedTokenIds.has(token.id)) {
      return { id: token.id, text: token.text, highlighted: false };
    }
    const selected = selectedWords.find((word) => word.originalTokenId === token.id);
    if (!selected) {
      return { id: token.id, text: '____', highlighted: false };
    }
    return { id: token.id, text: selected.text, highlighted: true };
  });

  const puzzleCompleted = status === 'success';
  const puzzleIncorrect = status === 'error';

  return (
    <div className={styles.puzzleSection} data-prevent-nav="true">
      <div className={styles.puzzleHeader}>
        <span className={styles.puzzleTitle}>句子拼图</span>
        <div className={styles.puzzleHeaderActions}>
          <div className={styles.sliderControl} data-prevent-nav="true">
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.05"
              value={maskRatio}
              onChange={(event) => onMaskRatioChange?.(Number(event.target.value))}
              aria-label="遮盖比例"
            />
            <span className={styles.sliderValue}>{Math.round(maskRatio * 100)}%</span>
          </div>
          <button
            type="button"
            className={styles.puzzleResetBtn}
            onClick={(event) => {
              event.stopPropagation();
              handleReset();
            }}
            data-prevent-nav="true"
          >
            重置
          </button>
          <button
            type="button"
            className={styles.puzzleResetBtn}
            onClick={(event) => {
              event.stopPropagation();
              handleShuffle();
            }}
            data-prevent-nav="true"
          >
            随机
          </button>
        </div>
      </div>
      <div className={styles.puzzleMaskedSentence}>
        {displayedSentenceFragments.map((fragment) => (
          <span
            key={fragment.id}
            className={fragment.highlighted ? styles.puzzleFilledWord : ''}
          >
            {fragment.text}
          </span>
        ))}
      </div>
      <div className={styles.puzzleInstructions}>
        从下方选择缺失的单词补全部分空格。
      </div>
      <div className={styles.puzzleWords} data-prevent-nav="true">
        {puzzleOptions.map((word) => (
          <button
            key={word.id}
            type="button"
            className={`${styles.puzzleWordButton} ${selectedWords.find((item) => item.id === word.id) ? styles.puzzleWordButtonUsed : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              handleSelectWord(word.id);
            }}
            disabled={!!selectedWords.find((item) => item.id === word.id)}
            data-prevent-nav="true"
          >
            {word.text}
          </button>
        ))}
      </div>
      <div className={styles.puzzleAnswer} data-prevent-nav="true">
        {selectedWords.length > 0 ? (
          selectedWords.map((word) => (
            <button
              key={`answer-${word.id}`}
              type="button"
              className={styles.puzzleAnswerWord}
              onClick={(event) => {
                event.stopPropagation();
                handleRemoveWord(word.id);
              }}
              data-prevent-nav="true"
            >
              {word.text}
            </button>
          ))
        ) : (
          <div className={styles.puzzlePlaceholder}>选择正确的单词填入空格。</div>
        )}
      </div>
      {puzzleCompleted && (
        <div className={`${styles.puzzleFeedback} ${styles.puzzleSuccess}`}>
          做得好，句子完整啦！
        </div>
      )}
      {puzzleIncorrect && (
        <div className={`${styles.puzzleFeedback} ${styles.puzzleError}`}>
          顺序不太对，再试试～
        </div>
      )}
    </div>
  );
};

const SentenceStudy = () => {
  const { bookId: legacyBookId, chapterId: routeChapterId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const queryBookId = searchParams.get('bookid') || searchParams.get('bookId');
  const queryChapterId = searchParams.get('chapterid') || searchParams.get('chapterId');
  const bookId = queryBookId || legacyBookId || location.state?.bookId || null;
  const chapterId = queryChapterId || routeChapterId || null;
  
  const [sentences, setSentences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [revealedSentences, setRevealedSentences] = useState(() => new Set());
  const [maskRatio, setMaskRatio] = useState(0.3);
  const [showWordModal, setShowWordModal] = useState(false);
  const [wordModalLookup, setWordModalLookup] = useState(null);
  const [wordModalLoading, setWordModalLoading] = useState(false);
  const [wordModalCollinsLoading, setWordModalCollinsLoading] = useState(false);
  const [wordModalActiveDict, setWordModalActiveDict] = useState('youdao');
  const initialStateRef = useRef(location.state);
  const sentenceCardRef = useRef(null);

  const fetchBookSentencesFallback = useCallback(async (targetBookId) => {
    const [favoritesResponse, novelResponse] = await Promise.all([
      api.get(withApiPrefix('favorite-sentences/')),
      api.get(`/novels/${targetBookId}`),
    ]);

    const favorites = Array.isArray(favoritesResponse.data) ? favoritesResponse.data : [];
    const novelData = novelResponse.data;

    if (!novelData || !Array.isArray(novelData.volumes) || novelData.volumes.length === 0) {
      return [];
    }

    const chapterOrderMap = new Map();
    novelData.volumes.forEach((volume, volumeIndex) => {
      if (!volume) {
        return;
      }
      const volumeOrderIndex = typeof volume.order_index === 'number' ? volume.order_index : volumeIndex;
      const chapters = Array.isArray(volume.chapters) ? volume.chapters : [];
      chapters.forEach((chapter, chapterIndex) => {
        if (!chapter || chapter.id == null) {
          return;
        }
        const normalizedChapterId = Number(chapter.id);
        if (!Number.isFinite(normalizedChapterId)) {
          return;
        }
        const chapterOrderIndex = typeof chapter.order_index === 'number' ? chapter.order_index : chapterIndex;
        const compoundOrder = volumeOrderIndex * 100000 + chapterOrderIndex;
        if (!chapterOrderMap.has(normalizedChapterId)) {
          chapterOrderMap.set(normalizedChapterId, {
            compoundOrder,
            volumeOrderIndex,
            chapterOrderIndex,
          });
        }
      });
    });

    if (chapterOrderMap.size === 0) {
      return [];
    }

    const filteredFavorites = favorites.filter((item) => {
      const rawChapterId = item?.chapter_id ?? item?.chapterId;
      const chapterId = Number(rawChapterId);
      if (!Number.isFinite(chapterId)) {
        return false;
      }
      return chapterOrderMap.has(chapterId);
    });

    filteredFavorites.sort((a, b) => {
      const chapterIdA = Number(a?.chapter_id ?? a?.chapterId);
      const chapterIdB = Number(b?.chapter_id ?? b?.chapterId);
      const orderA = chapterOrderMap.get(chapterIdA)?.compoundOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = chapterOrderMap.get(chapterIdB)?.compoundOrder ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      const sentenceIdA = Number(a?.sentence_id ?? a?.sentenceId ?? 0);
      const sentenceIdB = Number(b?.sentence_id ?? b?.sentenceId ?? 0);
      if (Number.isFinite(sentenceIdA) && Number.isFinite(sentenceIdB) && sentenceIdA !== sentenceIdB) {
        return sentenceIdA - sentenceIdB;
      }
      return 0;
    });

    return filteredFavorites;
  }, []);

  useEffect(() => {
    if (location.state) {
      initialStateRef.current = location.state;
    }
  }, [location.state]);

  // 从URL获取当前索引
  useEffect(() => {
    const indexParam = searchParams.get('index');
    if (indexParam != null) {
      const parsed = parseInt(indexParam, 10);
      if (!Number.isNaN(parsed) && parsed >= 0) {
        setCurrentIndex(parsed);
        return;
      }
    }
    setCurrentIndex(0);
  }, [searchParams]);

  // 获取句子列表
  useEffect(() => {
    const stateSentences = location.state?.sentences;
    if (Array.isArray(stateSentences) && stateSentences.length) {
      setSentences(stateSentences);
      setError('');
      setLoading(false);
      return;
    }

    const fetchSentences = async () => {
      try {
        if (!bookId) {
          setSentences([]);
          setError('暂无收藏句子');
          setLoading(false);
          return;
        }

        setLoading(true);
        setError('');

        const endpoint = chapterId
          ? withApiPrefix(`favorite-sentences/by-chapter/${chapterId}`)
          : withApiPrefix(`favorite-sentences/by-book/${bookId}`);

        const response = await api.get(endpoint);
        console.log('favorite sentences by book response:', response.data);
        const sentencesData = Array.isArray(response.data) ? response.data : [];

        if (!chapterId && bookId && sentencesData.length === 0) {
          const fallbackSentences = await fetchBookSentencesFallback(bookId);
          setSentences(fallbackSentences);
          setRevealedSentences(new Set());
          if (fallbackSentences.length === 0) {
            setError('暂无收藏句子');
          }
          return;
        }

        setSentences(sentencesData);
        setRevealedSentences(new Set());

        if (sentencesData.length === 0) {
          setError('暂无收藏句子');
        }
      } catch (err) {
        console.error('加载句子失败:', err);
        if (!chapterId && bookId) {
          try {
            const fallbackSentences = await fetchBookSentencesFallback(bookId);
            setSentences(fallbackSentences);
            setRevealedSentences(new Set());
            if (fallbackSentences.length === 0) {
              setError('暂无收藏句子');
            }
            return;
          } catch (fallbackError) {
            console.error('通过fallback加载句子失败:', fallbackError);
          }
        }
        setSentences([]);
        setError('加载句子失败，请稍后再试');
      } finally {
        setLoading(false);
      }
    };

    fetchSentences();
  }, [bookId, chapterId, location.state, fetchBookSentencesFallback]);

  useEffect(() => {
    setRevealedSentences(new Set());
  }, [bookId]);

  const updateIndexParam = useCallback((value) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('index', String(value));
      return params;
    });
  }, [setSearchParams]);

  const handleNext = () => {
    if (!sentences.length) {
      return;
    }

    const nextIndex = (currentIndex + 1) % sentences.length;
    setCurrentIndex(nextIndex);
    updateIndexParam(nextIndex);
  };

  const handlePrevious = () => {
    if (!sentences.length) {
      return;
    }

    const nextIndex = (currentIndex - 1 + sentences.length) % sentences.length;
    setCurrentIndex(nextIndex);
    updateIndexParam(nextIndex);
  };

  const handleToggleEnglish = () => {
    const key = getSentenceKey(currentSentence);
    if (!key) return;

    setRevealedSentences((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleBack = () => {
    const initialState = initialStateRef.current;

    if (initialState?.from === 'historySentences') {
      navigate('/study/sentences');
      return;
    }

    if (initialState?.from === 'chapterLearn' && initialState?.returnPath) {
      navigate(initialState.returnPath);
      return;
    }

    const params = new URLSearchParams(location.search);
    const bookQuery =
      params.get('bookid') ||
      params.get('bookId') ||
      initialState?.bookId ||
      bookId;
    if (bookQuery) {
      navigate(`/study/sentences?bookid=${encodeURIComponent(bookQuery)}`);
      return;
    }

    if (bookId) {
      navigate(`/book?bookid=${bookId}`);
      return;
    }

    navigate(-1);
  };

  const handleUnfavorite = async () => {
    const currentSentence = sentences[currentIndex];
    const sentenceId = getSentenceKey(currentSentence);
    if (!sentenceId) return;

    setFavoriteLoading(true);
    try {
      await api.delete(withApiPrefix(`favorite-sentences/${sentenceId}`));
      
      // 从列表中移除该句子
      const newSentences = sentences.filter((_, idx) => idx !== currentIndex);
      setSentences(newSentences);
      setRevealedSentences((prev) => {
        if (!prev.has(sentenceId)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(sentenceId);
        return next;
      });
      
      // 如果还有句子，调整当前索引
      if (newSentences.length > 0) {
        const newIndex = Math.min(currentIndex, newSentences.length - 1);
        setCurrentIndex(newIndex);
        updateIndexParam(newIndex);
      } else {
        // 没有句子了，返回上一页
        handleBack();
      }
    } catch (error) {
      console.error('取消收藏失败:', error);
      alert('取消收藏失败，请稍后重试');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const currentSentence = sentences[currentIndex];
  const sentenceKey = getSentenceKey(currentSentence);
  const isEnglishRevealed = sentenceKey ? revealedSentences.has(sentenceKey) : false;
  const englishContentId = sentenceKey ? `sentence-english-${sentenceKey}` : undefined;

  const renderLoadingState = () => (
    <div className={styles.container}>
      <LoadingSpinner fullPage text="加载句子中..." />
    </div>
  );

  const renderEmptyState = () => (
    <div className={styles.container}>
      <div className={styles.error}>
        <p>{error || '暂无句子'}</p>
        <button onClick={handleBack} type="button" className={styles.backBtn} aria-label="返回收藏句子列表">
          <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
        </button>
      </div>
    </div>
  );

  if (loading) {
    return renderLoadingState();
  }

  if (error || sentences.length === 0) {
    return renderEmptyState();
  }

  const handleWordClick = async (rawWord) => {
    const trimmed = typeof rawWord === 'string' ? rawWord.trim() : '';
    if (!trimmed) {
      return;
    }

    setWordModalLoading(true);
    setWordModalCollinsLoading(true);
    setShowWordModal(true);
    setWordModalActiveDict('youdao');

    try {
      const [youdaoResponse, collinsResponse] = await Promise.all([
        api.get(`/dict/youdao/lookup/${encodeURIComponent(trimmed)}`).catch(() => ({ data: { success: false } })),
        api.get(`/dict/collins/lookup/${encodeURIComponent(trimmed)}`).catch(() => ({ data: { success: false } })),
      ]);

      setWordModalLookup({
        word: trimmed,
        success: youdaoResponse.data?.success !== false || collinsResponse.data?.success !== false,
        youdao: youdaoResponse.data,
        collins: collinsResponse.data,
      });
    } catch (err) {
      console.error('查询单词失败:', err);
      setWordModalLookup({
        word: trimmed,
        success: false,
      });
    } finally {
      setWordModalLoading(false);
      setWordModalCollinsLoading(false);
    }
  };

  const handleSentenceAreaClick = (event) => {
    if (showWordModal) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    const interactiveTarget = event.target.closest(
      'button, a, input, textarea, select, label, [data-prevent-nav="true"]'
    );
    if (interactiveTarget) {
      return;
    }

    const card = sentenceCardRef.current;
    if (!card) {
      return;
    }

    const rect = card.getBoundingClientRect();
    if (!rect || rect.width === 0) {
      return;
    }

    const clickX = event.clientX - rect.left;

    if (!Number.isFinite(clickX)) {
      return;
    }

    if (clickX < rect.width / 2) {
      handlePrevious();
    } else {
      handleNext();
    }
  };

  const closeWordModal = () => {
    setShowWordModal(false);
    setWordModalLookup(null);
    setWordModalActiveDict('youdao');
  };

  const renderEnglishContent = () => {
    if (!currentSentence?.content_en) {
      return null;
    }

    return currentSentence.content_en.split(/(\s+)/).map((part, index) => {
      if (/^\s+$/.test(part)) {
        return part;
      }

      const cleaned = part.replace(/[^A-Za-z'-]/g, '');
      if (!cleaned) {
        return part;
      }

      return (
        <span
          key={`word-${sentenceKey}-${index}`}
          className={styles.clickableWord}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            handleWordClick(cleaned);
          }}
        >
          {part}
        </span>
      );
    });
  };

  return (
    <div className={styles.container}>
      {/* 头部导航 */}
      <div className={styles.header}>
        <button onClick={handleBack} type="button" className={styles.backBtn} aria-label="返回收藏句子列表">
          <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
        </button>
        <div className={styles.headerRight}>
          <div className={styles.progress}>
            {currentIndex + 1} / {sentences.length}
          </div>
        <button
          onClick={handleUnfavorite}
          disabled={favoriteLoading}
          className={styles.unfavoriteBtn}
          title="取消收藏"
          aria-label="取消收藏"
        >
          {favoriteLoading ? (
            <LoaderIcon size={16} />
          ) : (
            <HeartIcon size={22} filled />
          )}
        </button>
        </div>
      </div>
      {/* 句子卡片 */}
      <div
        className={styles.sentenceCard}
        ref={sentenceCardRef}
        onClick={handleSentenceAreaClick}
      >
        {/* <div className={styles.cardHeader}>
          <span className={styles.sentenceLabel}>
            句子 {currentSentence.sentence_id ?? currentSentence.sentenceId ?? '未知'}
          </span>
        </div> */}

        <div className={styles.sentenceContent}>
          {currentSentence.content_zh && (
            <div className={styles.chineseText}>
              {currentSentence.content_zh}
            </div>
          )}

          {currentSentence.content_en && (
            <div className={styles.englishSection}>
              <button
                type="button"
                className={`${styles.englishToggleBtn} ${isEnglishRevealed ? styles.englishToggleBtnActive : ''}`}
                onClick={handleToggleEnglish}
                aria-pressed={isEnglishRevealed}
                aria-controls={englishContentId}
              >
                {isEnglishRevealed ? '隐藏英文' : '我想看看英文表达'}
              </button>
              {isEnglishRevealed ? (
                <div id={englishContentId} className={styles.englishText}>
                  {renderEnglishContent()}
                </div>
              ) : (
                <div id={englishContentId} className={styles.englishHint}>
                  想一想：你会如何用英文表达这句话？点击上方按钮查看答案。
                </div>
              )}
            </div>
          )}

          <SentencePuzzle
            sentenceKey={sentenceKey}
            contentEn={currentSentence?.content_en}
            maskRatio={maskRatio}
            onMaskRatioChange={setMaskRatio}
          />
        </div>


      </div>



      {/* 导航按钮 */}

      <WordModal
        isOpen={showWordModal}
        wordLookup={wordModalLookup}
        dictResults={wordModalLookup ? {
          youdao: wordModalLookup.youdao,
          collins: wordModalLookup.collins,
        } : {}}
        dictLoading={{ youdao: wordModalLoading, collins: wordModalCollinsLoading }}
        favoriteLoading={false}
        isFavorited={false}
        onPlayPronunciation={() => {}}
        onFavoriteWord={() => {}}
        onDictTabClick={(dict) => setWordModalActiveDict(dict)}
        onClose={closeWordModal}
        onWordLookup={(word) => handleWordClick(word)}
        activeDict={wordModalActiveDict}
      />
    </div>
  );
};

export default SentenceStudy;
